import { lookupAirportCoords } from "./airportCoords";
import { resolveAirportRunway } from "./airportRunways";
import { AIRPORTS, lookupAirport, sceneryFromRecord } from "./airports";
import type { SceneryAirport, SceneryPresence } from "./types";

/**
 * Top-level Community folder / manifest heuristic. Content.xml, when readable,
 * only drops Community packages this profile has disabled. Official and
 * streamed packages with no folder under Community are not scanned. Does not
 * read scenery.cfg, layout.json, or BGL. ICAOs are 4-letter airport-like
 * tokens — catalog / Destinations hits always count; common pack tokens are
 * ignored. Paste / Browse Community path walks that folder.
 */
export const SCENERY_SCAN_LIMITS =
  "Top-level Community folder names and those packages’ manifest.json / package.json titles, including bespoke packs and Marketplace downloads in that folder. Content.xml (Store LocalCache or gamertag subfolder; Steam AppData / LocalCache) drops Community folders this profile has disabled. It does not add Official or streamed packages that are not folders under this Community path. A folder or manifest with no ICAO token does not invent one — add that field by hand on Destinations. Does not read scenery.cfg, layout.json, or BGL. ICAOs are 4-letter airport-like tokens (catalog / hand-added Destinations hits always count; leftover scan stubs do not rematch; pack tokens like ORBX / MSFS are ignored). Rescan fully replaces this desk’s inventory. Paste / Browse Community path walks that folder when Content.xml is missing.";

/** Exact 4-letter tokens bounded by non-letters (not a sliding window). */
const TOKEN_RE = /(?:^|[^A-Za-z0-9])([A-Za-z]{4})(?=[^A-Za-z0-9]|$)/g;

/**
 * Common Community / publisher tokens that collide with ICAO-shaped codes.
 * Catalog / Destinations membership still wins over this list.
 */
export const ICAO_TOKEN_DENYLIST = new Set([
  "MSFS",
  "ASBO",
  "ORBX",
  "XMLA",
  "XMLB",
  "BASE",
  "CITY",
  "LAND",
  "TREE",
  "PACK",
  "MODS",
  "PLUS",
  "PROD",
  "STUD",
  "WORK",
  "TRUE",
  "REAL",
  "HIGH",
  "LITE",
  "FULL",
  "FREE",
  "PAID",
  "DEMO",
  "TEST",
  "DATA",
  "DROP",
  "DRAG",
  "DRAW",
  "DRIP",
  "DRUM",
  "FILE",
  "AUTO",
  "USER",
  "CUST",
  "PREM",
  "GOLD",
  "NEXT",
  "MESH",
  "TEXT",
  "MODE",
  "CORE",
  "GAME",
  "DESK",
  "MENU",
  "THEM",
  "DARK",
  "NIGH",
  "DAWN",
  "DUSK",
  "SEAS",
  "WINT",
  "SUMM",
  "FALL",
  "SPRI",
  "GOLF",
  "BEAC",
  "COAS",
  "LAKE",
  "RIVE",
  "FORE",
  "SNOW",
  "RAIN",
  "WIND",
  "CLOU",
  "YEAR",
  "KEEP",
  "KIND",
  "KING",
  "KNOW",
  "KNOT",
  "KILO",
  "PATH",
  "PAGE",
  "PARK",
  "PALM",
  "PART",
  "PAST",
  "LIST",
  "LIVE",
  "LINE",
  "LINK",
  "GOOD",
  "GONE",
  "DATE",
  "DAYS",
  "ORIG",
  "OVER",
  "TRIM",
  "TRIP",
  "NONE",
  "PORT",
  "AIRP",
  "SCEN",
  "COMM",
  "CONT",
  "JSON",
  "MANI",
  "LAYO",
  "FROM",
  "WITH",
  "THIS",
  "THAT",
  "HAVE",
  "BEEN",
  "WILL",
  "YOUR",
  "MORE",
  "SOME",
  "THAN",
  "WHEN",
  "THEN",
  "ONLY",
  "ALSO",
  "INTO",
  "JUST",
  "LIKE",
  "BACK",
  "MAKE",
  "TIME",
  "BEST",
  "EACH",
  "MOST",
  "LONG",
  "WELL",
  "SIMS",
  "SIMU",
  "FLYT",
  "AERO",
  "WORL",
  "ENVI",
  "NATU",
  "ULTR",
  "MEGA",
  "SUPE",
  "HYPE",
  "ULTI",
  "PLAT",
  "SILV",
  "BRON",
  "STAN",
  "FOLD",
  "NAME",
  "TYPE",
  "TOOL",
  "UTIL",
  "LIBR",
  "OBJE",
  "IMAG",
  "SATL",
  "PHOT",
  "INIB",
  "ASOB",
]);

/** Two-letter ICAO national prefixes (K / Y / U are also accepted as 1-letter). */
const ICAO_NATIONAL_PREFIXES = new Set([
  "CY",
  "CZ",
  "CE",
  "CF",
  "CT",
  "CW",
  "CR",
  "CS",
  "EB",
  "ED",
  "EE",
  "EF",
  "EG",
  "EH",
  "EI",
  "EK",
  "EL",
  "EN",
  "EP",
  "ES",
  "ET",
  "EV",
  "EY",
  "LA",
  "LB",
  "LC",
  "LD",
  "LE",
  "LF",
  "LG",
  "LH",
  "LI",
  "LJ",
  "LK",
  "LL",
  "LM",
  "LN",
  "LO",
  "LP",
  "LQ",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LW",
  "LX",
  "LY",
  "LZ",
  "DA",
  "DB",
  "DF",
  "DG",
  "DI",
  "DN",
  "DR",
  "DT",
  "DX",
  "FA",
  "FB",
  "FC",
  "FD",
  "FE",
  "FG",
  "FH",
  "FI",
  "FJ",
  "FK",
  "FL",
  "FM",
  "FN",
  "FO",
  "FP",
  "FQ",
  "FS",
  "FT",
  "FV",
  "FW",
  "FX",
  "FY",
  "FZ",
  "GA",
  "GB",
  "GC",
  "GE",
  "GF",
  "GG",
  "GL",
  "GM",
  "GO",
  "GQ",
  "GS",
  "GU",
  "GV",
  "HA",
  "HB",
  "HC",
  "HD",
  "HE",
  "HH",
  "HK",
  "HL",
  "HR",
  "HS",
  "HT",
  "HU",
  "OA",
  "OB",
  "OE",
  "OI",
  "OJ",
  "OK",
  "OL",
  "OM",
  "OO",
  "OP",
  "OR",
  "OS",
  "OT",
  "OY",
  "MB",
  "MD",
  "MG",
  "MH",
  "MK",
  "MM",
  "MN",
  "MP",
  "MR",
  "MS",
  "MT",
  "MU",
  "MW",
  "MY",
  "PA",
  "PF",
  "PG",
  "PH",
  "PJ",
  "PK",
  "PL",
  "PM",
  "PO",
  "PP",
  "PT",
  "PW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SF",
  "SG",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SP",
  "SS",
  "SU",
  "SV",
  "SW",
  "SY",
  "TA",
  "TB",
  "TD",
  "TF",
  "TG",
  "TI",
  "TJ",
  "TK",
  "TL",
  "TN",
  "TQ",
  "TR",
  "TT",
  "TU",
  "TV",
  "TX",
  "RC",
  "RJ",
  "RK",
  "RO",
  "RP",
  "VA",
  "VC",
  "VD",
  "VE",
  "VG",
  "VH",
  "VI",
  "VL",
  "VM",
  "VN",
  "VO",
  "VQ",
  "VR",
  "VT",
  "VV",
  "VY",
  "WA",
  "WB",
  "WI",
  "WM",
  "WP",
  "WQ",
  "WR",
  "WS",
  "ZB",
  "ZG",
  "ZH",
  "ZJ",
  "ZL",
  "ZP",
  "ZS",
  "ZU",
  "ZW",
  "ZY",
  "ZK",
  "AY",
  "AG",
  "AN",
  "NS",
  "NT",
  "NV",
  "NW",
  "NZ",
  "BG",
  "BI",
  "BK",
]);

const MANIFEST_STRING_KEYS = ["title", "creator", "content_type", "manufacturer", "description", "name"] as const;

export type SceneryScanSource = {
  name: string;
  texts?: string[];
};

export type SceneryScanCounts = {
  packageCount: number;
  icaoCount: number;
  usedContentXml: boolean;
};

function knownSet(knownIcaos?: Iterable<string>): Set<string> {
  return new Set([...(knownIcaos ?? [])].map((c) => c.toUpperCase()));
}

/** Destinations row created only by Community / Content.xml detect — not hand-added or starter. */
export const COMMUNITY_SCAN_STUB_NOTES = "From Community scan";

export function isCommunityScanStub(row: Pick<SceneryAirport, "notes">): boolean {
  return (row.notes ?? "").trim().startsWith(COMMUNITY_SCAN_STUB_NOTES);
}

export function collectKnownIcaos(destinations: SceneryAirport[] = []): string[] {
  const set = new Set<string>();
  for (const apt of AIRPORTS) set.add(apt.icao.toUpperCase());
  for (const dest of destinations) {
    const icao = dest.icao.trim().toUpperCase();
    if (icao.length === 4) set.add(icao);
  }
  return [...set];
}

/** Known set for a disk scan — catalog + hand-added / starter Destinations. Scan stubs must not rematch ghosts. */
export function collectScanKnownIcaos(destinations: SceneryAirport[] = []): string[] {
  return collectKnownIcaos(destinations.filter((row) => !isCommunityScanStub(row)));
}

export function isDeniedIcaoToken(token: string): boolean {
  return ICAO_TOKEN_DENYLIST.has(token.trim().toUpperCase());
}

/** Plausible airport ICAO: Kxxx / Yxxx / Uxxx or a known 2-letter national prefix. */
export function isPlausibleAirportIcao(token: string): boolean {
  const code = token.trim().toUpperCase();
  if (!/^[A-Z]{4}$/.test(code)) return false;
  if (isDeniedIcaoToken(code)) return false;
  if (code.startsWith("K") || code.startsWith("Y") || code.startsWith("U")) return true;
  return ICAO_NATIONAL_PREFIXES.has(code.slice(0, 2));
}

export function delimitedFourLetterTokens(text: string): string[] {
  const hits: string[] = [];
  const seen = new Set<string>();
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(text))) {
    const token = match[1]?.toUpperCase();
    if (!token || seen.has(token)) continue;
    seen.add(token);
    hits.push(token);
  }
  return hits;
}

export function extractIcaosFromText(text: string, knownIcaos?: Iterable<string>): string[] {
  const known = knownSet(knownIcaos);
  const hits = new Set<string>();
  for (const token of delimitedFourLetterTokens(text)) {
    if (known.has(token) || isPlausibleAirportIcao(token)) hits.add(token);
  }
  return [...hits];
}

export function extractIcaosFromFolderName(name: string, knownIcaos?: Iterable<string>): string[] {
  return extractIcaosFromText(name, knownIcaos);
}

export function extractIcaosFromManifestJson(raw: string, knownIcaos?: Iterable<string>): string[] {
  const fields: string[] = [];
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const key of MANIFEST_STRING_KEYS) {
      const value = parsed[key];
      if (typeof value === "string" && value.trim()) fields.push(value);
    }
  } catch {
    if (raw.trim()) fields.push(raw);
  }
  const hits = new Set<string>();
  for (const field of fields) {
    for (const icao of extractIcaosFromText(field, knownIcaos)) hits.add(icao);
  }
  return [...hits];
}

export function scanCommunityFolderNames(
  folderNames: string[],
  knownIcaos: string[] = [],
  now = new Date(),
): SceneryPresence[] {
  return scanCommunitySources(
    folderNames.map((name) => ({ name })),
    knownIcaos,
    now,
  );
}

export function scanCommunitySources(
  sources: SceneryScanSource[],
  knownIcaos: string[] = [],
  now = new Date(),
): SceneryPresence[] {
  const byIcao = new Map<string, SceneryPresence>();
  const scannedAtUtc = now.toISOString();
  for (const src of sources) {
    const folderName = src.name.split(/[/\\]/).filter(Boolean).pop() ?? src.name;
    for (const icao of extractIcaosFromFolderName(folderName, knownIcaos)) {
      if (!byIcao.has(icao)) {
        byIcao.set(icao, { icao, source: "folder_name", folderName, scannedAtUtc });
      }
    }
    for (const text of src.texts ?? []) {
      for (const icao of extractIcaosFromManifestJson(text, knownIcaos)) {
        if (!byIcao.has(icao)) {
          byIcao.set(icao, { icao, source: "manifest", folderName, scannedAtUtc });
        }
      }
    }
  }
  return [...byIcao.values()].sort((a, b) => a.icao.localeCompare(b.icao));
}

export function uniqueIcaosFromInventory(inventory: SceneryPresence[]): string[] {
  return [...new Set(inventory.map((row) => row.icao.toUpperCase()).filter(Boolean))].sort();
}

export function folderNamesFromRelativePaths(paths: string[]): string[] {
  const names = new Set<string>();
  for (const path of paths) {
    const parts = path.split(/[/\\]/).filter(Boolean);
    for (const part of parts) {
      if (part.includes(".")) continue;
      names.add(part);
    }
    if (parts[0]) names.add(parts[0]);
  }
  return [...names];
}

export function topLevelFolderNamesFromRelativePaths(paths: string[]): string[] {
  const names = new Set<string>();
  for (const path of paths) {
    const first = path.split(/[/\\]/).filter(Boolean)[0];
    if (first && !first.includes(".")) names.add(first);
  }
  return [...names];
}

export function sceneryStubFromIcao(icao: string): SceneryAirport {
  const code = icao.trim().toUpperCase();
  const rec = lookupAirport(code);
  if (rec) {
    const base = sceneryFromRecord(rec);
    const extra = base.notes.trim() ? ` — ${base.notes.trim()}` : "";
    return { ...base, notes: `${COMMUNITY_SCAN_STUB_NOTES}${extra}` };
  }
  const coords = lookupAirportCoords(code);
  const runway = resolveAirportRunway(code, { notes: COMMUNITY_SCAN_STUB_NOTES });
  return {
    id: code,
    icao: code,
    name: code,
    city: code,
    country: "—",
    runwayFt: runway.longestFt,
    notes: COMMUNITY_SCAN_STUB_NOTES,
    lat: coords?.lat,
    lon: coords?.lon,
  };
}

function icaoSet(icaos: Iterable<string>): Set<string> {
  return new Set([...icaos].map((raw) => raw.trim().toUpperCase()).filter(Boolean));
}

/** Add discovered ICAOs onto Destinations without removing manual / existing rows. */
export function mergeDiscoveredIcaosIntoScenery(
  scenery: SceneryAirport[],
  icaos: Iterable<string>,
): SceneryAirport[] {
  const have = new Set(scenery.map((s) => s.icao.toUpperCase()));
  const extras: SceneryAirport[] = [];
  for (const raw of icaos) {
    const code = raw.trim().toUpperCase();
    if (!code || have.has(code)) continue;
    extras.push(sceneryStubFromIcao(code));
    have.add(code);
  }
  return extras.length ? [...scenery, ...extras] : scenery;
}

export function dropCommunityScanStubs(scenery: SceneryAirport[]): SceneryAirport[] {
  return scenery.filter((row) => !isCommunityScanStub(row));
}

/**
 * Full replace of this desk’s scan stubs. Hand-added / starter Destinations stay.
 * ICAOs no longer on the current Community-folder scan are removed.
 */
export function replaceDiscoveredIcaosInScenery(
  scenery: SceneryAirport[],
  icaos: Iterable<string>,
): SceneryAirport[] {
  return mergeDiscoveredIcaosIntoScenery(dropCommunityScanStubs(scenery), icaoSet(icaos));
}

/**
 * Nanode / career Pull: company allow-list syncs; remote scan stubs never land.
 * This desk’s inventory is re-stubbed so a friend’s scan cannot overwrite Emerson’s.
 */
export function applyRemoteDestinationsKeepLocalScan(
  incoming: SceneryAirport[],
  localInventoryIcaos: Iterable<string>,
): SceneryAirport[] {
  return replaceDiscoveredIcaosInScenery(dropCommunityScanStubs(incoming), localInventoryIcaos);
}

export function formatSceneryScanNote(opts: {
  prefix?: string;
  packageCount: number;
  icaoCount: number;
  usedContentXml?: boolean;
}): string {
  const prefix = opts.prefix ?? "Scan";
  const packs = opts.packageCount;
  const icaos = opts.icaoCount;
  const xmlBit = opts.usedContentXml ? " Content.xml filtered disabled Community packages." : "";
  return `${prefix}: Scanned ${packs} Community package${packs === 1 ? "" : "s"} · matched ${icaos} ICAO token${icaos === 1 ? "" : "s"} (folders under this Community path — not Official or streamed, and not the Destinations allow-list or catalog).${xmlBit}`;
}

export function formatAirportInventoryCounts(opts: {
  scannedIcaoCount: number;
  destinationCount: number;
  catalogCount: number;
  stubCount?: number;
}): string {
  const stubs =
    opts.stubCount && opts.stubCount > 0 ? ` · ${opts.stubCount} scan stub${opts.stubCount === 1 ? "" : "s"}` : "";
  return `This desk’s scan: ${opts.scannedIcaoCount} ICAO${opts.scannedIcaoCount === 1 ? "" : "s"} · Destinations allow-list: ${opts.destinationCount}${stubs} · catalog: ${opts.catalogCount}. Scanned ≠ Destinations ≠ catalog.`;
}

export function sceneryScanCounts(inventory: SceneryPresence[], packageCount: number, usedContentXml = false): SceneryScanCounts {
  return { packageCount, icaoCount: inventory.length, usedContentXml };
}
